import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {FormLabel} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {obsStyles} from '../obsStyle';

function ObsNeuroLimbMovementArms(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [movement, setMovement] = React.useState(props.movement);
    const [arm, setArm] = React.useState(props.arm);
    const [secondMovement, setSecondMovement] = React.useState(props.secondMovement);
    const [secondArm, setSecondArm] = React.useState(props.secondArm);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);
    const [optionalArm, setOptionalArm] = React.useState(props.optionalArm);

    const [movements, setMovements] = React.useState([]);

    useEffect(() => {
        let items = [];
        if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._16_99 || ageRangeYears === obsAges._5_16 || ageRangeYears === obsAges._1_5) {
                items = [
                    {
                        id: '1',
                        description: t('Normal Power'),
                        // default: true,
                    },
                    {
                        id: '2',
                        description: t('Mild Weakness'),
                    },
                    {
                        id: '3',
                        description: t('Severe Weakness'),
                    },
                    {
                        id: '4',
                        description: t('Spastic Flexion'),
                    },
                    {
                        id: '5',
                        description: t('Extension'),
                    },
                    {
                        id: '6',
                        description: t('No Response'),
                    },
                ];
            }
        }
        setMovements(items);
    }, [type]);

    function setDefaults() {
        let def = _.find(movements, (item) => !!item.default);
        // if (def) {
        setMovement(def);
        setArm(null);
        setOptionalArm(config.neuroObsLimbMovExpanded);
        setSecondMovement(def);
        setSecondArm(null);
        // }
    }

    useEffect(() => {
        if (!movement) setDefaults();

    }, [movements]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (((movement && arm) || (optionalArm && secondMovement && secondArm)) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) acceptObs();
    }, [movement, arm, optionalArm, secondMovement, secondArm, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    movement,
                    arm,
                    secondMovement,
                    secondArm,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        movement,
        arm,
        secondMovement,
        secondArm,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            // if (movement) {
            //     newScore = movement.score;
            // }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, movement, arm, secondMovement, secondArm]);

    const onMovementChange = (value) => {
        setMovement(value);
        clearAccepted();
    };

    const onArmChange = (value) => {
        setArm(value);
        if (value === 'left') {
            setSecondArm('right');
        } else if (value === 'right') {
            setSecondArm('left');
        } else {
            setSecondArm(null);
        }
        clearAccepted();
    };

    const onSecondMovementChange = (value) => {
        setSecondMovement(value);
        clearAccepted();
    };

    const onSecondArmChange = (value) => {
        setSecondArm(value);
        if (value === 'left') {
            setArm('right');
        } else if (value === 'right') {
            setArm('left');
        }
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    const onAddOptionalArm = () => {
        setOptionalArm(!optionalArm);
        setSecondMovement(_.find(movements, (item) => !!item.default));
        setSecondArm(null);
        clearAccepted();
    };

    const buttons = [];
    if (!config.neuroObsLimbMovExpanded) {
        buttons.push(
            <AgyleButton
                key={'limb-arm-add'}
                size={'normal'}
                variant="outlined"
                colour="primary"
                onClick={onAddOptionalArm}
            >
                {optionalArm ? t('Remove Optional Arm') : t('Add Optional Arm')}
            </AgyleButton>,
        );
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12}>
                        <AgyleBlockSwitch
                            onChange={(value) => onArmChange(value)}
                            value={arm}
                            // disabled={props.extraOption1Disabled || !props.allAccepted}
                            customLabels={{
                                yes: t('Left Arm'),
                                no: t('Right Arm'),
                            }}
                            valueObject={{
                                true: 'left',
                                false: 'right',
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} className={classes.component} style={{marginLeft: 10}}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Limb Movement - Arms')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onMovementChange(value)}
                                    options={movements}
                                    value={movement}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {optionalArm &&
                        <>
                            <Grid item xs={12}>
                                <AgyleBlockSwitch
                                    onChange={(value) => onSecondArmChange(value)}
                                    value={secondArm}
                                    // disabled={props.extraOption1Disabled || !props.allAccepted}
                                    customLabels={{
                                        yes: t('Left Arm'),
                                        no: t('Right Arm'),
                                    }}
                                    valueObject={{
                                        true: 'left',
                                        false: 'right',
                                    }}
                                    style={{
                                        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} className={classes.component} style={{marginLeft: 10}}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{marginBottom: 15}}
                                >
                                    <Grid item xs={12}>
                                        <FormLabel className={classes.label}>
                                            {t('Limb Movement - Arms')}
                                        </FormLabel>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <AgyleDictionaryField
                                            onChange={(_, value) => onSecondMovementChange(value)}
                                            options={movements}
                                            value={secondMovement}
                                            getOptionLabel={(option) => option.description}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            extended
                                            noBorder={false}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    }

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled:
                            !(movement && arm) ||
                            (optionalArm && !(secondMovement && secondArm)),
                        hideStatuses: true,
                        disableAcceptButtons,
                        customButtons: buttons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroLimbMovementArms.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    movement: PropTypes.object,
    optionalArm: PropTypes.bool,
    arm: PropTypes.oneOf([null, 'left', 'right']),
    secondMovement: PropTypes.object,
    secondArm: PropTypes.oneOf([null, 'left', 'right']),
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsNeuroLimbMovementArms.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    movement: null,
    optionalArm: false,
    arm: null,
    secondMovement: null,
    secondArm: null,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsNeuroLimbMovementArms);